import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';

import { LeftPanelCloseOutlined } from '../../../../../Common/components/Icons/icons';
import EncounterViewDialog from '../Encounter/EncounterViewDialog';
import SoapPastTable from './SoapPastTable';
import { sampleEncounters } from './data';

import {
  expandButtonSx,
  expandIconSx,
  soapPastHeaderSx,
  soapPastRootSx,
  soapPastTitleSx
} from './SoapPastStyles';

const SoapPast = ({ isPast, setPast, encounters, onSignEncounter }) => {
  const [openEncounter, setOpenEncounter] = useState(false);

  const [selectedEncounter, setSelectedEncounter] = useState(null);

  const [localEncounters, setLocalEncounters] = useState(sampleEncounters);

  const rows = useMemo(() => {
    const combined = [
      ...encounters,

      ...localEncounters.filter(
        sample => !encounters.some(item => item.id === sample.id)
      )
    ];

    return [...combined].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [encounters, localEncounters]);

  const latestEncounterId = rows.length > 0 ? rows[0].id : null;

  const handlePastEncounterClick = encounter => {
    setSelectedEncounter(encounter);
    setOpenEncounter(true);
  };

  const handleEncounterClose = () => {
    setOpenEncounter(false);
    setSelectedEncounter(null);
  };

  const handleSignEncounter = signedEncounter => {
    if (!signedEncounter) {
      return;
    }

    setLocalEncounters(previous =>
      previous.map(item =>
        item.id === signedEncounter.id ? signedEncounter : item
      )
    );

    onSignEncounter(signedEncounter);

    setOpenEncounter(false);
    setSelectedEncounter(null);
  };

  const isSelectedEncounterSigned = selectedEncounter?.status === 'signed';

  return (
    <>
      <Box sx={soapPastRootSx}>
        <Box sx={soapPastHeaderSx}>
          <Typography sx={soapPastTitleSx}>Past SOAP Notes</Typography>

          {!isPast && (
            <IconButton
              size="small"
              onClick={setPast}
              aria-label="Expand past SOAP notes"
              sx={expandButtonSx}>
              <LeftPanelCloseOutlined sx={expandIconSx} />
            </IconButton>
          )}
        </Box>

        <SoapPastTable
          rows={rows}
          latestEncounterId={latestEncounterId}
          onEncounterClick={handlePastEncounterClick}
        />
      </Box>

      <EncounterViewDialog
        open={openEncounter}
        encounter={selectedEncounter}
        readOnly={isSelectedEncounterSigned}
        onClose={handleEncounterClose}
        onDelete={() => {}}
        onCopy={() => {}}
        onEdit={() => {}}
        onSign={handleSignEncounter}
      />
    </>
  );
};

SoapPast.propTypes = {
  isPast: PropTypes.bool,

  setPast: PropTypes.func,

  encounters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

      date: PropTypes.string,
      patientName: PropTypes.string,
      patientInfo: PropTypes.string,
      subjective: PropTypes.string,
      objective: PropTypes.string,
      assessmentPlan: PropTypes.string,
      vitals: PropTypes.object,

      diagnoses: PropTypes.array,

      prescription: PropTypes.object,
      labOrder: PropTypes.object,
      imagingOrder: PropTypes.object,
      physicianOrder: PropTypes.object,

      status: PropTypes.oneOf(['draft', 'signed']),

      signedBy: PropTypes.string
    })
  ),

  onSignEncounter: PropTypes.func
};

SoapPast.defaultProps = {
  isPast: false,
  setPast: () => {},
  encounters: [],
  onSignEncounter: () => {}
};

export default SoapPast;
